import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Plus,
  Search,
  CreditCard,
  Building,
  ArrowUpRight,
  ArrowDownLeft,
} from 'lucide-react';
import { AccountItem, PaymentItem } from '../types/index.ts';

interface AccountsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const AccountsPage: React.FC<AccountsPageProps> = ({ onNavigate }) => {
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAccountsData = async () => {
    try {
      setLoading(true);
      const [aRes, pRes] = await Promise.all([
        fetch('/api/accounts'),
        fetch('/api/payments'),
      ]);
      if (aRes.ok) {
        const aData = await aRes.json();
        if (Array.isArray(aData)) setAccounts(aData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) setPayments(pData);
      }
    } catch (err) {
      console.warn('Accounts fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountsData();
  }, []);

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Accounts &amp; Banking Ledger
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900">
              Double-Entry General Ledger
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Real-time balance of bank accounts (HBL, Meezan), petty cash, receivables and payment vouchers.
          </p>
        </div>

        <button
          onClick={() => onNavigate('expenses')}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors self-start sm:self-auto"
        >
          <span>Record Site Expense →</span>
        </button>
      </div>

      {/* Chart of Accounts Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(Array.isArray(accounts) ? accounts : []).map((acc) => (
          <div key={acc.id} className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold text-blue-900">{acc.code}</span>
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-slate-100 rounded text-slate-700">
                {acc.type}
              </span>
            </div>

            <div>
              <h3 className="font-bold text-sm text-slate-900">{acc.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{acc.description}</p>
            </div>

            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">Current Balance:</span>
              <span className="font-bold text-sm text-slate-900">{formatPKR(acc.balance)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Transactions Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Recent Payments &amp; Settlement Vouchers
          </span>
          <span className="text-xs text-slate-500">{payments.length} Records</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Voucher #</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Bank / Account</th>
                <th className="py-3 px-4">Cheque / RTGS Ref</th>
                <th className="py-3 px-4">Remarks</th>
                <th className="py-3 px-4 text-right">Amount (PKR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(Array.isArray(payments) ? payments : []).map((p) => {
                const isReceived = p.paymentType === 'RECEIVED';
                return (
                  <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-4 font-mono font-bold text-blue-900">{p.paymentNumber}</td>
                    <td className="py-3 px-4 text-slate-600">{p.paymentDate}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                          isReceived ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {isReceived ? <ArrowDownLeft className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                        <span>{p.paymentType}</span>
                      </span>
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-800">{p.accountName}</td>
                    <td className="py-3 px-4 font-mono text-slate-600">{p.chequeOrRefNo || 'N/A'}</td>
                    <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{p.remarks}</td>
                    <td
                      className={`py-3 px-4 text-right font-bold ${
                        isReceived ? 'text-emerald-700' : 'text-slate-900'
                      }`}
                    >
                      {formatPKR(p.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
